import {DeskInfo, FloorModel} from "./FloorModel";
import type {ImageSize} from "./FloorPlanViewer";
import {grey} from "@mui/material/colors";
import {ReactNode, useMemo} from "react";
import {numberCmp} from "../../util/SortUtil";
import {Avatar, AvatarProps, Stack, Tooltip, Typography} from "@mui/material";
import NeighbourhoodColorIcon from "../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import DeskChip from "../../components/desk-chip/DeskChip";
import EmployeeAvatar from "../../components/employee-avatar/EmployeeAvatar";
import {Desk, INeighbourhood} from "../../api/Model";
import {andFilter, applyFilter, filterNeighbourhoods} from "../../util/FilterUtil";
import {Opt} from "../../util/Optional";
import {filterNotNull} from "../../util/ArrayUtil";
import {viridisShade} from "../../util/ColorUtil";

const MAX_RADIUS_RATIO = 0.030;
const MIN_RADIUS_RATIO = 0.006;
const NEIGHBOUR_GAP_RATIO = 0.3;
const DESK_STROKE_WIDTH = 2;

export type FloorDeskFilter = {
    neighbourhoods: Opt<INeighbourhood[]>,
};

export type FloorDeskViewMode = 'assignments' | 'usage';

export const emptyFloorDeskFilter = {
    neighbourhoods: undefined,
}

function filterFloorDesks(floorModel: FloorModel, filter: FloorDeskFilter): readonly Desk[] {
    return applyFilter(floorModel.floorDesks, andFilter(
        filterNeighbourhoods(
            filter.neighbourhoods,
            desk => filterNotNull([floorModel.deskModel.neighbourhood(desk)]),
        ),
    ));
}

export function noDeskEmployeesPerShareDesk(deskInfo: DeskInfo & {type: 'shared'}) {
    return Math.round(deskInfo.neighbourhoodSharing.noDeskEmployees.length / deskInfo.neighbourhoodSharing.sharedDesks.length * 10) / 10;
}

function DeskTooltipFigure({figure, title, description}: {
    figure: ReactNode,
    title?: ReactNode,
    description: ReactNode,
}) {
    return (
        <Stack direction="column" sx={{mt: 2}}>
            <Typography variant="body1">
                {title && (
                    `${title}: `
                )}
                <Typography component="span" variant="h5">{figure}</Typography>
            </Typography>
            <Typography variant="body2" color="grey.400">
                {description}
            </Typography>
        </Stack>
    );
}

function DeskTooltip({desk, deskInfo}: {
    desk: Desk,
    deskInfo: DeskInfo,
}) {
    return (
        <Stack direction="column" gap={1}>
            <DeskChip desk={desk} sx={{width: "fit-content", alignSelf: "center"}}/>
            {deskInfo.neighbourhood ? (
                <Stack direction="row" gap={1} alignItems="center">
                    <NeighbourhoodColorIcon neighbourhood={deskInfo.neighbourhood}/>
                    <Typography variant="body1">
                        {deskInfo.neighbourhood.name}
                    </Typography>
                </Stack>
            ) : (
                <Typography variant="body1" color="grey.400">
                    No neighbourhood
                </Typography>
            )}
            {deskInfo.type === 'assigned' ? (
                <>
                    <Stack direction="row" gap={1} alignItems="center">
                        <EmployeeAvatar employee={deskInfo.employee} sx={{width: 24, height: 24}}/>
                        <Typography variant="body1">
                            {deskInfo.employee.fullName}
                        </Typography>
                    </Stack>
                    <DeskTooltipFigure
                        figure={Number.isNaN(deskInfo.visits.percent) ? '—' : `${deskInfo.visits.percent}%`}
                        title="Desk utilization"
                        description={`${deskInfo.employee.fullName} visited the office ${deskInfo.visits.workDayVisits} times of ${deskInfo.visits.tenureDayCount} workdays`}
                    />
                </>
            ) : deskInfo.type === 'shared' ? (
                <>
                    <DeskTooltipFigure
                        figure={noDeskEmployeesPerShareDesk(deskInfo)}
                        title="Ratio"
                        description={`${deskInfo.neighbourhoodSharing.noDeskEmployees.length} neighbourhood employees without an assigned desk per ${deskInfo.neighbourhoodSharing.sharedDesks.length} shared desks`}
                    />
                    <DeskTooltipFigure
                        figure={`${deskInfo.neighbourhoodSharing.percent}%`}
                        title="Desk utilization"
                        description={`Neighbourhood employees without an assigned desk visited the office ${deskInfo.neighbourhoodSharing.deskDayUsage} times in ${deskInfo.neighbourhoodSharing.dayCount} days`}
                    />
                </>
            ) : deskInfo.type === 'overflow' ? (
                <Typography variant="body1" color="grey.400">
                    Overflow desk
                </Typography>
            ) : null}
        </Stack>
    );
}

function DeskCircle({text, deskColor, deskRadius, ...props}: {
    text: ReactNode,
    deskColor: string,
    deskRadius: number,
} & AvatarProps) {
    const deskSize = deskRadius * 2;
    return (
        <Avatar
            {...props}
            sx={{
                width: deskSize,
                height: deskSize,
                backgroundColor: deskColor,
                color: theme => theme.palette.getContrastText(deskColor),
                fontWeight: 'bold',
                fontSize: deskRadius,
                ...props.sx
            }}
        >
            {text}
        </Avatar>
    );
}


function moveMidpoint(d: number): (x: number) => number {
    return x => (1 - d) * x /
        ((1 - d) * x + d * (1 - x));
}

const midpoint75 = moveMidpoint(0.75);

function percentColor(percent: number): string {
    const boundedPercent = Math.max(0, Math.min(Number.isNaN(percent) ? 0 : percent, 100));
    return viridisShade(1 - midpoint75(boundedPercent / 100));
}

export function FloorDesks({floorModel, imageSize, filter, viewMode}: {
    floorModel: FloorModel,
    imageSize: ImageSize,
    filter: FloorDeskFilter,
    viewMode: FloorDeskViewMode,
}) {
    const deskRadius = useMemo(
        () => adaptiveDeskRadius(floorModel.floorDesks, imageSize),
        [floorModel.floorDesks, imageSize],
    );

    const filteredDesks = useMemo(
        () => filterFloorDesks(floorModel, filter),
        [floorModel, filter],
    );

    return (
        <>{filteredDesks.map(desk => {
            if (desk.xPos == null || desk.yPos == null) {
                return null;
            }

            const deskInfo = floorModel.deskInfo(desk);
            const position = normalizedPositionToPixels(desk.xPos, desk.yPos, imageSize);
            const deskSize = deskRadius * 2;
            const deskColor = deskInfo.neighbourhood?.displayColor || grey[600];

            return (
                <Tooltip
                    key={desk.id}
                    arrow
                    placement="top"
                    enterDelay={100}
                    title={
                        <DeskTooltip desk={desk} deskInfo={deskInfo}/>
                    }
                >
                    <g>
                        <circle
                            cx={position.x}
                            cy={position.y}
                            r={deskRadius}
                            fill={deskColor}
                            stroke={viewMode === 'assignments' ? deskColor : undefined}
                            strokeWidth={viewMode === 'assignments' ? DESK_STROKE_WIDTH : undefined}
                            vectorEffect="non-scaling-stroke"
                        />
                        <foreignObject
                            x={position.x - deskRadius}
                            y={position.y - deskRadius}
                            width={deskSize}
                            height={deskSize}
                            overflow="visible"
                        >
                            {viewMode === 'assignments' ? (
                                deskInfo.type === 'assigned' ? (
                                    <EmployeeAvatar
                                        employee={deskInfo.employee}
                                        sx={{
                                            width: deskSize,
                                            height: deskSize,
                                            boxShadow: `0 0 40px 5px ${deskColor}`,
                                        }}
                                    />
                                ) : deskInfo.type === 'shared' ? (
                                    <DeskCircle
                                        text={noDeskEmployeesPerShareDesk(deskInfo)}
                                        deskColor={deskColor}
                                        deskRadius={deskRadius}
                                    />
                                ) : deskInfo.type === 'overflow' ? (
                                    <DeskCircle text="OF" deskColor={deskColor} deskRadius={deskRadius}/>
                                ) : null
                            ) : (
                                deskInfo.type === 'assigned' ? (
                                    <DeskCircle
                                        text={Number.isNaN(deskInfo.visits.percent) ? '—' : deskInfo.visits.percent}
                                        deskColor={percentColor(deskInfo.visits.percent)}
                                        deskRadius={deskRadius}
                                        sx={{color: 'black'}}
                                    />
                                ) : deskInfo.type === 'shared' ? (
                                    <DeskCircle
                                        text={deskInfo.neighbourhoodSharing.percent}
                                        deskColor={percentColor(deskInfo.neighbourhoodSharing.percent)}
                                        deskRadius={deskRadius}
                                        sx={{color: 'black'}}
                                    />
                                ) : deskInfo.type === 'overflow' ? (
                                    <DeskCircle
                                        text="OF"
                                        deskColor={percentColor(0)}
                                        deskRadius={deskRadius}
                                    />
                                ) : null
                            )}
                        </foreignObject>
                    </g>
                </Tooltip>
            );
        })}</>
    );
}

export function adaptiveDeskRadius(
    desks: readonly { xPos?: number | null, yPos?: number | null }[],
    imageSize: ImageSize,
) {
    const shortImageEdge = Math.min(imageSize.width, imageSize.height);
    const maximumRadius = shortImageEdge * MAX_RADIUS_RATIO;
    const minimumRadius = shortImageEdge * MIN_RADIUS_RATIO;
    const positions = desks.flatMap(desk => desk.xPos == null || desk.yPos == null
        ? []
        : [normalizedPositionToPixels(desk.xPos, desk.yPos, imageSize)]
    );

    if (positions.length < 2) {
        return maximumRadius;
    }

    const nearestNeighbourDistances = positions
        .map((position, index) =>
            Math.min(...positions
                .filter((_, otherIndex) => otherIndex !== index)
                .map(other => Math.hypot(position.x - other.x, position.y - other.y)))
        )
        .sort(numberCmp((it: number) => it));
    const lowerDecileIndex = Math.floor((nearestNeighbourDistances.length - 1) * 0.1);
    const radiusForDeskSpacing = nearestNeighbourDistances[lowerDecileIndex] * NEIGHBOUR_GAP_RATIO;

    return Math.max(minimumRadius, Math.min(maximumRadius, radiusForDeskSpacing));
}

export function normalizedPositionToPixels(x: number, y: number, imageSize: ImageSize) {
    return {
        x: x * imageSize.width,
        y: y * imageSize.height,
    };
}
